/**
 * Das Kartierverfahren: nach welcher Anleitung ein Punkt aufgenommen ist.
 *
 * bpweb ist als App für die forstliche Standortserkundung nach SEA 95 entstanden.
 * Daneben soll die Aufnahme nach der Bodenkundlichen Kartieranleitung (KA) möglich
 * werden (Plan: PLAN-ka6-kartierverfahren.md). Diese Datei führt dafür nur den
 * BEGRIFF ein und liest ihn; sie ändert nichts am bestehenden Verhalten.
 *
 * Die Regeln:
 *
 * 1. **Fehlt der Vermerk, ist es SEA 95.** Alle Projekte und Punkte von vor der
 *    Einführung tragen keinen Vermerk, und neue SEA-Punkte bekommen auch keinen.
 *    So bleibt ein SEA-Punkt Zeichen für Zeichen derselbe Datensatz wie bisher, und
 *    es braucht keine Umstellung der gespeicherten Daten.
 *
 * 2. **Ein Punkt sagt selbst, wie er zu lesen ist.** Es gibt EINE Punkt-Datenbank
 *    für die ganze App, und Ansichten wie "Alle Bohrpunkte" zeigen Punkte über
 *    Projektgrenzen hinweg. Das Projekt entscheidet deshalb nur, welchen Vermerk ein
 *    NEUER Punkt bekommt ({@link vermerkFuerNeuenPunkt}); gelesen wird immer am
 *    Punkt ({@link verfahrenVon}).
 *
 * 3. **Ein unbekannter Vermerk ist nicht SEA 95.** Schreibt eine neuere Fassung der
 *    App ein Verfahren, das diese Fassung nicht kennt, darf der Punkt nicht als
 *    SEA-Punkt gelesen werden: Die Maske zeigte leere SEA-Felder, und ein Speichern
 *    schriebe SEA-Angaben in eine fremde Aufnahme. Deshalb gibt es die dritte
 *    Antwort `'unbekannt'`; wer sie bekommt, zeigt den Punkt an, ändert ihn aber nicht.
 */

/** Die Verfahren, die diese Fassung der App kennt. */
export type Kartierverfahren = 'sea95' | 'ka';

/** Die Auflagen der Bodenkundlichen Kartieranleitung, die sich im Bogen unterscheiden. */
export type KaAuflage = 5 | 6;

/** Was beim Lesen herauskommen kann, siehe Regel 3. */
export type GelesenesVerfahren = Kartierverfahren | 'unbekannt';

/** Gilt, wo kein Vermerk steht (Regel 1). */
export const VERFAHREN_VORGABE: Kartierverfahren = 'sea95';

/** Gilt für ein KA-Projekt ohne Angabe der Auflage: die aktuelle, 6. Auflage von 2024. */
export const KA_AUFLAGE_VORGABE: KaAuflage = 6;

/**
 * Der Vermerk, wie er am Projekt und am Punkt steht. Bewusst weit getypt
 * (`string`, `number`): Gelesen wird, was in der Datenbank steht, und das kann
 * aus einer neueren Fassung der App stammen.
 */
export interface MitVerfahren {
	verfahren?: string;
	kaAuflage?: number;
}

const BEKANNTE_VERFAHREN: readonly string[] = ['sea95', 'ka'];

/**
 * Nach welchem Verfahren ist dieses Projekt oder dieser Punkt zu lesen?
 *
 * Fehlt der Vermerk (oder gibt es den Träger gar nicht), ist es SEA 95. Steht ein
 * Vermerk da, den diese Fassung nicht kennt, ist die Antwort `'unbekannt'`.
 */
export function verfahrenVon(traeger: MitVerfahren | null | undefined): GelesenesVerfahren {
	const v = traeger?.verfahren;
	if (v === undefined || v === null || v === '') return VERFAHREN_VORGABE;
	return BEKANNTE_VERFAHREN.includes(v) ? (v as Kartierverfahren) : 'unbekannt';
}

/**
 * Die Auflage einer KA-Aufnahme. `undefined` bei allem, was keine KA-Aufnahme ist:
 * Eine Auflage an einem SEA-Punkt wäre eine Auskunft ohne Sinn.
 */
export function kaAuflageVon(traeger: MitVerfahren | null | undefined): KaAuflage | undefined {
	if (verfahrenVon(traeger) !== 'ka') return undefined;
	const a = traeger?.kaAuflage;
	return a === 5 || a === 6 ? a : KA_AUFLAGE_VORGABE;
}

/**
 * Der Vermerk, den ein NEUER Punkt in diesem Projekt bekommt.
 *
 * Für SEA 95 ist das ein leeres Objekt (Regel 1: ein SEA-Punkt trägt keinen
 * Vermerk). Für ein Projekt mit unbekanntem Verfahren wird NICHT geraten, sondern
 * abgebrochen: Ein Punkt, der unter falschem Verfahren angelegt wird, lässt sich
 * hinterher nicht mehr richtig lesen.
 */
export function vermerkFuerNeuenPunkt(projekt: MitVerfahren | null | undefined): MitVerfahren {
	const v = verfahrenVon(projekt);
	if (v === 'unbekannt') {
		throw new Error(
			`Das Projekt verwendet ein Kartierverfahren ("${projekt?.verfahren}"), das diese Fassung der App nicht kennt. `
			+ 'Bitte die App aktualisieren, bevor hier Punkte angelegt werden.',
		);
	}
	if (v === 'sea95') return {};
	return { verfahren: 'ka', kaAuflage: kaAuflageVon(projekt) };
}

/** Klartext für Oberfläche, Druck und Export. */
export function verfahrenName(traeger: MitVerfahren | null | undefined): string {
	const v = verfahrenVon(traeger);
	if (v === 'sea95') return 'SEA 95';
	if (v === 'ka') return `KA ${kaAuflageVon(traeger)}`;
	return 'unbekanntes Verfahren';
}

// ---------------------------------------------------------------------------
// Die Wahl im Fenster "Neues Projekt"
// ---------------------------------------------------------------------------

/** Verfahren und Auflage in einem Wert, so wie man sie im Fenster auswählt. */
export type VerfahrenWahl = 'sea95' | 'ka5' | 'ka6';

export interface VerfahrenWahlEintrag {
	wahl: VerfahrenWahl;
	name: string;
	erklaerung: string;
}

/**
 * Was beim Anlegen eines Projekts zur Wahl steht.
 *
 * Die 5. Auflage der KA fehlt mit Absicht (Nutzer-Entscheidung 20.09.2026: Ziel ist
 * die 6. Auflage, die 5. erst auf Nachfrage). Das Datenmodell trägt sie schon; es
 * fehlen ihr Feldumfang und ihre Listen.
 */
export const VERFAHREN_WAHLEN: readonly VerfahrenWahlEintrag[] = [
	{
		wahl: 'sea95',
		name: 'SEA 95',
		erklaerung: 'Forstliche Standortserkundung nach der Standortserkundungsanleitung.',
	},
	{
		wahl: 'ka6',
		name: 'KA 6',
		erklaerung: 'Bodenaufnahme nach der Bodenkundlichen Kartieranleitung, 6. Auflage.',
	},
];

/** Die Wahl, die dem Stand dieses Projekts entspricht. `undefined` bei unbekanntem Verfahren. */
export function verfahrenWahlVon(projekt: MitVerfahren | null | undefined): VerfahrenWahl | undefined {
	const v = verfahrenVon(projekt);
	if (v === 'sea95') return 'sea95';
	if (v === 'ka') return kaAuflageVon(projekt) === 5 ? 'ka5' : 'ka6';
	return undefined;
}

/**
 * Schreibt die Wahl ans Projekt. Für SEA 95 werden beide Felder ENTFERNT, nicht
 * auf 'sea95' gesetzt: Ein SEA-Projekt sieht damit genauso aus wie alle Projekte
 * von vor der Einführung (Regel 1).
 */
export function schreibeVerfahrenWahl(projekt: Record<string, unknown>, wahl: VerfahrenWahl): void {
	if (wahl === 'sea95') {
		delete projekt['verfahren'];
		delete projekt['kaAuflage'];
		return;
	}
	projekt['verfahren'] = 'ka';
	projekt['kaAuflage'] = wahl === 'ka5' ? 5 : 6;
}
